import React, { useMemo, useState } from "react";
import { Card, List, Button, Popconfirm, Spin, Modal, Table, Statistic, Row, Col, Divider, message } from "antd";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import trans from "../../utils";
import {
  useGetSavedReportsQuery,
  useDeleteSavedReportMutation,
  useRunAnalyticsQueryMutation,
} from "../../Redux/service/Analytics/Analytics";

export default function AnalyticsSavedReports() {
  const { data, isLoading } = useGetSavedReportsQuery();
  const [deleteReport] = useDeleteSavedReportMutation();
  const [runQuery, { data: runData, isLoading: isRunning }] = useRunAnalyticsQueryMutation();

  const [viewReport, setViewReport] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const reports = data?.data || [];
  const result = runData?.data || { columns: [], rows: [], cards: [] };

  const tableColumns = useMemo(
    () =>
      result.columns.map((col) => ({
        title: col.label,
        dataIndex: col.key,
        key: col.key,
      })),
    [result.columns]
  );

  async function handleView(report) {
    setViewReport(report);
    setIsModalOpen(true);
    try {
      await runQuery(report.query).unwrap();
    } catch (err) {
      message.error(err?.data?.message || trans("Failed to run report"));
    }
  }

  function handleClose() {
    setIsModalOpen(false);
    setViewReport(null);
  }

  return (
    <Card title={trans("Saved Reports")} style={{ margin: 16 }}>
      <Spin spinning={isLoading}>
        <List
          dataSource={reports}
          renderItem={(report) => (
            <List.Item
              actions={[
                <Button key="view" icon={<EyeOutlined />} type="primary" ghost onClick={() => handleView(report)}>
                  {trans("View")}
                </Button>,
                <Popconfirm
                  key="delete"
                  title={trans("Delete this report?")}
                  onConfirm={() => deleteReport(report.id)}
                >
                  <Button icon={<DeleteOutlined />} danger>
                    {trans("Delete")}
                  </Button>
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta title={report.name} description={new Date(report.updatedAt).toLocaleString()} />
            </List.Item>
          )}
        />
      </Spin>

      <Modal
        title={viewReport?.name || trans("Report")}
        open={isModalOpen}
        onCancel={handleClose}
        footer={[
          <Button key="close" onClick={handleClose}>
            {trans("Close")}
          </Button>,
        ]}
        width={900}
      >
        <Spin spinning={isRunning}>
          {result.cards?.length > 0 && (
            <>
              <Divider orientation="left">{trans("Dashboard Cards")}</Divider>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {result.cards.map((card) => (
                  <Col key={card.key} xs={24} sm={12} md={8} lg={6}>
                    <Card>
                      <Statistic title={card.label} value={card.value ?? "-"} />
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {result.columns.length > 0 && (
            <>
              <Divider orientation="left">{trans("Details")}</Divider>
              <Table
                columns={tableColumns}
                dataSource={result.rows.map((row, index) => ({ key: index, ...row }))}
                scroll={{ x: true }}
              />
            </>
          )}
        </Spin>
      </Modal>
    </Card>
  );
}
